import * as React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Tooltip } from './tooltip';

/**
 * ONE ROW'S WORTH OF "THE SERVER REFUSED THIS LINE", rendered ON the line.
 *
 * The failure this exists to end: an error arrives in a banner at the top of
 * the screen, the row that caused it is not marked, and the office reads
 * "something failed" and guesses. On a fifteen-row TNT ticket the banner is
 * close to a thousand characters of joined-together prose and the guess is
 * expensive.
 *
 * Two call paths feed this, and the props are deliberately shaped for the
 * harder of the two rather than for the one adopting it first:
 *
 *  - the ticket editors' 400 `details[]` — `{ index, field, message }`, keyed
 *    by position in the submitted line-item array;
 *  - the pay-assignment save's 400 `refusals[]` — `{ index, ticketLineItemId,
 *    userId, reason, message }`, where a refusal can carry a REMEDY (the
 *    override hatch) and, for most reasons, deliberately must not.
 *
 * So `reasons` is a LIST, not a string, and `remedy` exists here even though
 * no current caller passes one. Both are explained below.
 */

/** One thing wrong with a row. `field` is optional — the pay path has no field. */
export interface RowErrorReason {
  message: string;
  /**
   * Which input the message is about, when the server said so.
   *
   * The ticket API sends this on every entry and the screens have always
   * dropped it. It is carried rather than rendered by default: the field is
   * already on screen next to the message on a row, and repeating its name reads
   * as noise. A caller that renders fields far apart can use it.
   */
  field?: string;
}

/**
 * An offered fix, rendered as a button beside the reason.
 *
 * WHY THIS IS OPTIONAL AND WHY IT MUST STAY OPTIONAL.
 *
 * On the pay path only one refusal reason has a remedy that belongs here.
 * `crew_ambiguous` is the row the costing genuinely cannot price — nothing on
 * a rate card supplies the missing crew size — and the override hatch is the
 * answer. `no_percentage_rate` is the opposite: the worker is on the wrong
 * basis and the fix is the Day Rate switch already on the row. Offering the
 * override there types a number over a question the system could have
 * answered, and afterwards nobody can tell that figure from a computed one.
 *
 * Hence: no default remedy, and no remedy derived from the reason string
 * inside this component. The caller decides, per reason, and a reason it does
 * not recognise gets none. Silence is the safe default — the failure mode this
 * guards is offering an override to a row that should not have one.
 */
export interface RowErrorRemedy {
  label: string;
  onClick: () => void;
  /** Long-form explanation of what the remedy does, for the button's tooltip. */
  title?: string;
  disabled?: boolean;
}

export interface RowErrorProps {
  /**
   * Every reason this row was refused.
   *
   * A list because the ticket API can return more than one entry for the same
   * index — a row with a bad rate AND a bad quantity produces two — and the
   * screens' old `rowErrors[d.index] = d.message` kept only the last, so the
   * office corrected one problem and got the same 400 back. A bare string is
   * accepted for the common single-reason call.
   */
  reasons: string | RowErrorReason[];
  /** An offered fix. Omit for reasons that should not be remedied here. */
  remedy?: RowErrorRemedy;
  /**
   * A VISIBLE prefix naming what happened, e.g. "Not saved: ".
   *
   * Present because the two call paths mean different things by a marked row
   * and the difference matters to the reader. On the ticket editors the row is
   * merely invalid and the sentence speaks for itself, so the severity is
   * announced to a screen reader and shown as the glyph. On a pay refusal
   * NOTHING WAS WRITTEN — the route returns before the insert — and that is not
   * inferable from "no percentage rate configured", so it is said out loud.
   *
   * When given it replaces the screen-reader-only prefix rather than adding to
   * it, or a reader hears both.
   */
  label?: string;
  className?: string;
}

/**
 * The edge treatment for a row that has an error.
 *
 * Returned as a class string rather than baked into a wrapper element because
 * the two ticket editors sit on different row backgrounds and neither should
 * lose it. This marks the row's edge and leaves its background alone.
 *
 * Spread onto the row the reason renders inside, so the mark and the words are
 * never separated.
 */
export function rowErrorRingClass(hasError: boolean): string {
  return hasError ? 'ring-1 ring-red-500/60' : '';
}

/**
 * COLOUR IS NOT THE ONLY SIGNAL, ON PURPOSE.
 *
 * Red alone fails for a colourblind reader and washes out on a tablet in
 * direct sun, which is exactly where the field editor is used. So every
 * refused row carries three redundant marks: the edge mark, a warning glyph, and
 * the words themselves. Any one of them surviving still says "this row".
 *
 * The glyph is decorative to a screen reader — it would otherwise announce
 * before the sentence it decorates — and the announcement comes from the live
 * region instead, which carries the reason text a reader actually needs.
 */
export function RowError({ reasons, remedy, label, className }: RowErrorProps) {
  const list: RowErrorReason[] =
    typeof reasons === 'string' ? [{ message: reasons }] : reasons;

  if (list.length === 0) return null;

  return (
    <div
      role="alert"
      data-testid="row-error"
      className={cn('mt-0.5 flex items-start gap-1.5 text-[11px] text-red-400', className)}
    >
      <AlertCircle aria-hidden="true" data-testid="row-error-icon" className="w-3.5 h-3.5 shrink-0 mt-[1px]" />
      <div className="min-w-0 flex-1 space-y-0.5">
        {list.map((r, i) => (
          <p key={i} className="leading-snug">
            {/*
              Spelled out for a screen reader because the glyph carrying this
              meaning visually is hidden from one, and "Rate must be a finite
              number" on its own does not announce as a failure.
            */}
            {i === 0 && (label
              ? <span className="font-semibold">{label}</span>
              : <span className="sr-only">Error: </span>)}
            {r.message}
          </p>
        ))}
        {remedy && (
          <Tooltip content={remedy.title}>
            <button
              type="button"
              onClick={remedy.onClick}
              disabled={remedy.disabled}
              className="underline underline-offset-2 hover:text-red-300 disabled:opacity-50 disabled:no-underline"
            >
              {remedy.label}
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/**
 * Collapse an API `details[]`/`refusals[]` array into per-row reason lists.
 *
 * Keyed by the `index` BOTH payloads carry, which is the row's position in the
 * array the client submitted — so the mapping only holds while the screen
 * renders rows in the sequence it sent them. Both ticket editors do.
 *
 * Accumulates rather than assigns. That single character is the bug described
 * on `reasons` above.
 */
export function groupRowErrors<T extends { index: number; message: string; field?: string }>(
  entries: T[]
): Record<number, RowErrorReason[]> {
  const out: Record<number, RowErrorReason[]> = {};
  for (const e of entries) {
    if (!Number.isInteger(e.index)) continue;
    (out[e.index] ||= []).push({ message: e.message, field: e.field });
  }
  return out;
}
